import { EndOfLife, EndOfLifeListResponse } from '@/types/devices';
import { OsType } from '@/components/DeviceOverview/types';
import { useGetOSEndOfLife } from './useGetOSEndOfLife';

const compareVersions = (a?: string, b?: string): number => {
  if (!a) return -1;
  if (!b) return 1;

  const aParts = a.split('.').map(Number);
  const bParts = b.split('.').map(Number);
  const maxLength = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < maxLength; i += 1) {
    const aNum = aParts[i] ?? 0;
    const bNum = bParts[i] ?? 0;
    if (aNum > bNum) return 1;
    if (aNum < bNum) return -1;
  }
  return 0;
};

const formatWindowsVersion = (version: string): string => {
  // Remove edition suffix (-e, -w) and replace dashes with spaces
  // "11-25h2-e" -> "11 25h2"
  const withoutEdition = version.replace(/-[ew]$/, '');
  const withSpaces = withoutEdition.replace(/-/g, ' ');
  return `Windows ${withSpaces}`;
};

const formatMacOSVersion = (codename?: string, latest?: string): string => {
  if (!codename || !latest) return '';
  return `macOS ${codename} ${latest}`;
};

/**
 *  Hook to get the latest OS versions from EOL data.
 */
export const useGetLatestOsVersions = () => {
  const { data: eolData, isLoading, isError } = useGetOSEndOfLife();

  let latestMacEntry: EndOfLife | undefined;
  let latestWindowsEntry: EndOfLife | undefined;

  const results = (eolData as EndOfLifeListResponse<EndOfLife> | undefined)
    ?.results;

  if (!isLoading && !isError && results?.length) {
    for (const entry of results) {
      if (entry.product === OsType.MACOS) {
        // For macOS, compare by version field
        if (
          !latestMacEntry ||
          compareVersions(entry.version, latestMacEntry.version) > 0
        ) {
          latestMacEntry = entry;
        }
      }
      if (entry.product === OsType.WINDOWS) {
        // For Windows, ignore entries with "lts" or "iot" in version
        if (entry.version.includes('lts') || entry.version.includes('iot')) {
          continue;
        }
        // Compare by latest field (only consider entries that have a latest field)
        if (
          entry.latest &&
          (!latestWindowsEntry ||
            !latestWindowsEntry.latest ||
            compareVersions(entry.latest, latestWindowsEntry.latest) > 0)
        ) {
          latestWindowsEntry = entry;
        }
      }
    }
  }

  const latestMacVersion = latestMacEntry
    ? formatMacOSVersion(latestMacEntry.codename, latestMacEntry.latest)
    : undefined;

  const latestWindowsVersion = latestWindowsEntry?.version
    ? formatWindowsVersion(latestWindowsEntry.version)
    : undefined;

  return {
    latest_macos_version: latestMacVersion,
    latest_windows_version: latestWindowsVersion,
    isLoading,
    isError,
  };
};
